import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/AppContext';
import {
    SttButtonFab,
    SttChipFileFormat,
    SttChipFileType,
    SttFileDialog,
    SttTable,
    UploadFileDialog,
} from '../../../sporttia/all';
import { updateElementInArray } from '../../../lib/utils';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type MshipData = {
    id: number;
};

type Props = {
    mship: MshipData;
};

type FileData = {
    id: number;
    name: string;
    type: string;
    format: string;
    created: string;
};

type FilesResponse = {
    rows?: FileData[];
    total?: number;
};

type PaginationParams = {
    page?: number;
    rows?: number;
};

export default function MshipDocuments({ mship }: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [userFiles, setUserFiles] = useState<FilesResponse>({});
    const [openFileId, setOpenFileId] = useState<number | null>();
    const [uploadFileOpen, setUploadFileOpen] = useState<boolean>(false);

    /**
     * Get user files.
     */
    const loadUserFiles = (p: PaginationParams = {}) => {
        cxt?.api('GET', `/mships/${mship.id}/files`, {
            params: {
                ...p,
            },
            success: (r: any) => {
                setUserFiles(r);
            },
        });
    };

    /**
     * Update file
     */
    const updateFile = (file: FileData) => {
        setUserFiles({
            ...userFiles,
            rows: updateElementInArray(userFiles.rows || [], file) as any,
        });
        setOpenFileId(null);
    };

    // -----| Render |-----
    return (
        <>
            <SttTable
                data={userFiles}
                columns={[
                    {
                        title: t('Created'),
                        field: 'created',
                        value: (row: FileData) =>
                            new DateBuilder(
                                row?.created,
                                cxt?.sc?.timezone?.name,
                                cxt?.lang?.key,
                            ).dmy(),
                    },
                    {
                        type: 'link',
                        align: 'left',
                        title: t('Name'),
                        value: (row: FileData) => row.name || t('Noname'),
                        onClick: (row: FileData) => setOpenFileId(row.id),
                    },
                    {
                        title: t('Type'),
                        field: 'type',
                        value: (row: FileData) => (
                            <SttChipFileType type={row.type} />
                        ),
                    },
                    {
                        title: t('Format'),
                        value: (row: FileData) => (
                            <SttChipFileFormat format={row.format} />
                        ),
                    },
                ]}
                onFetch={loadUserFiles}
            />

            <SttButtonFab
                onClick={() => setUploadFileOpen(true)}
                permission={PERMISSIONS.CUSTOMER.MODIFY}
            />

            <UploadFileDialog
                open={uploadFileOpen}
                types={['USER_DOC']}
                uploadParams={{ idMship: mship.id }}
                onClose={() => setUploadFileOpen(false)}
                onUploaded={() => {
                    setUploadFileOpen(false);
                    loadUserFiles();
                }}
            />

            <SttFileDialog
                idFile={openFileId as any}
                onClose={() => setOpenFileId(null)}
                onUpdate={updateFile as any}
            />
        </>
    );
}
